import { IfAbility } from '@efdigital/ll-addon-auth';
import { CoachCard, CoachesBrowse } from '@efdigital/ll-addon-coaching';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import type { CoachProfile } from '@efdigital/ll-addon-data/types/coaching/coach-profile';
import { CreateCoach } from '@efdigital/ll-admin-v2/components/coaching/create-coach.gts';
import {
  EditContext,
  EditingStoppedReason,
} from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { defaultNewCoachProfileAttributes } from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback';
import type { ModelFrom } from '@efdigital/ll-admin-v2/lib/type-utils';
import type CoachingCoachesIndexRoute from '@efdigital/ll-admin-v2/routes/coaching/coaches/index';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import BsModal from 'ember-bootstrap/components/bs-modal';
import { pageTitle } from 'ember-page-title';

interface CoachingCoachesIndexSignature {
  Args: {
    model: ModelFrom<CoachingCoachesIndexRoute>;
    controller: unknown;
  };
}

function cardColor(status: {
  hasPendingChanges: boolean;
  awaitingApproval: boolean;
}): string {
  if (status.hasPendingChanges) {
    return 'warning';
  }
  if (status.awaitingApproval) {
    return 'danger';
  }
  return 'primary';
}

export default class CoachingCoachesIndexTemplate extends Component<CoachingCoachesIndexSignature> {
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked modalOpen = false;
  @tracked newCoach?: CoachProfile;

  showCreateModal = () => {
    this.newCoach = this.store.createRecord<CoachProfile>(
      DataTypeCoaching.CoachProfile,
      defaultNewCoachProfileAttributes() as Partial<CoachProfile>
    );
    this.modalOpen = true;
  };

  hideCreateModal = () => {
    this.modalOpen = false;
    this.newCoach = undefined;
  };

  coachCreated = (reason: EditingStoppedReason) => {
    if (reason === EditingStoppedReason.Saved) {
      const userId = this.newCoach?.user?.trim();
      if (userId) {
        this.hideCreateModal();
        this.router.transitionTo('coaching.coaches.coach', userId);
        return;
      }
    }

    // EditRecord already reverts on cancel; only roll back for other close paths
    // (e.g. save failed) where the draft record may still be in the store.
    if (reason !== EditingStoppedReason.Cancelled && this.newCoach) {
      deepRollback(this.newCoach, this.store);
    }
    this.hideCreateModal();
  };

  <template>
    {{pageTitle "Coaches"}}
    <div class="container py-5">
      <IfAbility @ability="coaching-coaches" @atLeast="viewer">
        <:if>
          <CoachesBrowse
            @coaches={{@model.coaches}}
            @showApprovalBadges={{true}}
          >
            <:headerActions>
              <IfAbility @ability="coaching-coaches" @atLeast="editor">
                <:if>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    data-test-new-coach
                    {{on "click" this.showCreateModal}}
                  >
                    <iconify-icon inline icon="ph:plus-bold" />
                    New Coach
                  </button>
                </:if>
              </IfAbility>
            </:headerActions>
            <:coach as |coach status|>
              {{#let (cardColor status) as |color|}}
                <CoachCard
                  @coach={{coach}}
                  @pendingChanges={{status.hasPendingChanges}}
                  @awaitingApproval={{status.awaitingApproval}}
                  @showDbsBadge={{true}}
                  @color={{color}}
                  class="h-100"
                  @cardClass="h-100 w-100 border rounded-4 shadow-sm"
                  @footerClass="d-flex flex-row justify-content-between gap-1 align-items-center border-top-0 bg-{{color}}-subtle"
                >
                  <:actions>
                    <LinkTo
                      class="btn btn-sm btn-{{color}}"
                      @route="coaching.coaches.coach"
                      @model={{coach.user}}
                    >
                      View profile
                    </LinkTo>
                  </:actions>
                </CoachCard>
              {{/let}}
            </:coach>
            <:empty>
              <IfAbility @ability="coaching-coaches" @atLeast="editor">
                <:if>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    {{on "click" this.showCreateModal}}
                  >Add the first coach</button>
                </:if>
              </IfAbility>
            </:empty>
          </CoachesBrowse>
        </:if>
        <:else>
          <div class="alert alert-warning" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p>You do not have permission to view coaches.</p>
            <p class="mb-0">Please contact your administrator if you believe you
              should have access.</p>
          </div>
        </:else>
      </IfAbility>

      <IfAbility @ability="coaching-coaches" @atLeast="editor">
        <:if>
          <BsModal
            @open={{this.modalOpen}}
            @onHidden={{this.hideCreateModal}}
            @backdropClose={{false}}
            @size="lg"
            as |modal|
          >
            <modal.header>
              <h3 class="modal-title h4">Create Coach</h3>
            </modal.header>
            <modal.body>
              {{#if this.newCoach}}
                <EditContext
                  @onEditingStopped={{this.coachCreated}}
                  as |context|
                >
                  <CreateCoach @item={{this.newCoach}} @context={{context}} />
                </EditContext>
              {{/if}}
            </modal.body>
          </BsModal>
        </:if>
      </IfAbility>
    </div>
  </template>
}
